import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import LowPriorityOutlinedIcon from '@mui/icons-material/LowPriorityOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import { SvgIconComponent } from '@mui/icons-material';
import { supervisorRoles, mentorRoles, ROLES } from '@shared/constants/roles';
import { ActivityLog, ActivityLogActionType, ActivityLogType, ActivityTone } from './types';
import { getIconByActivityLogType } from '@services/icons';

const TYPE_LABELS: Record<ActivityLogType, string> = {
  ACTIVITY_CHAT: 'Video Chat',
  ACTIVITY_EXERCISE: 'Exercise',
  ACTIVITY_LESSON: 'Lesson',
  ACTIVITY_POLL: 'Patient Form',
  ACTIVITY_PRACTICE: 'Practice',
  CHAT: 'Video Chat',
  EXERCISE: 'Exercise',
  LESSON: 'Lesson',
  POLL: 'Patient Form',
  PRACTICE: 'Practice',
  MENTOR_FORM: 'Mentor Form',
};

const ACTION_LABELS: Record<ActivityLogActionType, string> = {
  [ActivityLogActionType.FEEDBACK]: 'Feedback',
  [ActivityLogActionType.MEANINGFUL_FEEDBACK]: 'Meaningful Feedback',
  [ActivityLogActionType.CHANGE_COURSE]: 'Course Change',
  [ActivityLogActionType.CHANGE_MENTOR]: 'Mentor Change',
  [ActivityLogActionType.CHANGE_STATUS]: 'Status Change',
  [ActivityLogActionType.MOVE_IN_PROGRAM]: 'Move in Program',
};

const ACTION_ICONS: Record<ActivityLogActionType, SvgIconComponent> = {
  [ActivityLogActionType.FEEDBACK]: FeedbackOutlinedIcon,
  [ActivityLogActionType.MEANINGFUL_FEEDBACK]: FeedbackOutlinedIcon,
  [ActivityLogActionType.CHANGE_COURSE]: SwapHorizOutlinedIcon,
  [ActivityLogActionType.CHANGE_MENTOR]: SupervisorAccountOutlinedIcon,
  [ActivityLogActionType.CHANGE_STATUS]: ToggleOnOutlinedIcon,
  [ActivityLogActionType.MOVE_IN_PROGRAM]: LowPriorityOutlinedIcon,
};

const ACTIVITY_TYPES = [
  ActivityLogType.LESSON,
  ActivityLogType.PRACTICE,
  ActivityLogType.ACTIVITY_CHAT,
  ActivityLogType.ACTIVITY_POLL,
  ActivityLogType.MENTOR_FORM,
];

export const activityTypeOptions = ACTIVITY_TYPES.map((value) => ({
  value,
  label: TYPE_LABELS[value],
}));

const SUCCESS_STATUSES = ['ACTIVE', 'COMPLETED', 'DONE', 'SUCCEEDED', 'SUBMITTED'];
const WARNING_STATUSES = ['INCOMPLETE', 'PARTIAL', 'PARTIALLY_COMPLETED', 'PENDING', 'SKIPPED'];
const ERROR_STATUSES = ['CANCELLED', 'ERROR', 'FAILED', 'MISSED'];

export const getActivityTypeIcon = (type?: ActivityLogActionType | ActivityLogType | null) => {
  if (!type) return EventNoteOutlinedIcon;
  return ACTION_ICONS[type as ActivityLogActionType] || getIconByActivityLogType(type as ActivityLogType);
};

export const getActivityTypeLabel = (type?: ActivityLogActionType | ActivityLogType | null) => {
  if (!type) return '';
  return ACTION_LABELS[type as ActivityLogActionType] || TYPE_LABELS[type as ActivityLogType] || '';
};

export const getStatusTone = (status: string): ActivityTone => {
  const normalized = status.toUpperCase().replace(/\s+/g, '_');
  if (SUCCESS_STATUSES.includes(normalized)) return 'success';
  if (WARNING_STATUSES.includes(normalized)) return 'warning';
  if (ERROR_STATUSES.includes(normalized)) return 'error';
  return 'neutral';
};

export const formatDuration = (duration: number) => {
  const minutes = Math.floor(duration / 60);
  const seconds = Math.round(duration % 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const getDuration = (startDate?: string, endDate?: string | null) => {
  if (!startDate || !endDate) return null;
  const duration = (new Date(endDate).getTime() - new Date(startDate).getTime()) / 1000;
  return formatDuration(duration);
};

export const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const OPEN_RECORD_TYPES: ActivityLogType[] = [
  ActivityLogType.EXERCISE,
  ActivityLogType.ACTIVITY_EXERCISE,
  ActivityLogType.PRACTICE,
  ActivityLogType.ACTIVITY_PRACTICE,
  ActivityLogType.POLL,
  ActivityLogType.ACTIVITY_POLL,
  ActivityLogType.MENTOR_FORM,
];

export const canOpenRecord = (activity?: ActivityLog | null) => !!activity && !!activity.type && OPEN_RECORD_TYPES.includes(activity.type);

const mapRecordTypeToActivityType = (type: ActivityLogType): ActivityLogType => {
  switch (type) {
    case ActivityLogType.LESSON:
      return ActivityLogType.ACTIVITY_LESSON;
    case ActivityLogType.EXERCISE:
      return ActivityLogType.ACTIVITY_EXERCISE;
    case ActivityLogType.PRACTICE:
      return ActivityLogType.ACTIVITY_PRACTICE;
    case ActivityLogType.POLL:
      return ActivityLogType.ACTIVITY_POLL;
    case ActivityLogType.CHAT:
      return ActivityLogType.ACTIVITY_CHAT;
    default:
      return type;
  }
};

export const normalizeActivityTypes = (activities: ActivityLog[]) =>
  activities.map((activity) => ({
    ...activity,
    type: activity.type ? mapRecordTypeToActivityType(activity.type) : null,
  }));


export const getActivityRoute = (activity: ActivityLog, userId: string) => {
  if (!activity.type || !canOpenRecord(activity)) return '';
  switch (activity.type) {
    case ActivityLogType.EXERCISE:
    case ActivityLogType.ACTIVITY_EXERCISE:
    case ActivityLogType.PRACTICE:
    case ActivityLogType.ACTIVITY_PRACTICE:
    case ActivityLogType.ACTIVITY_POLL:
    case ActivityLogType.POLL:
      return `/activity/${activity.type}/${activity.activityId || activity.externalId}`;
    case ActivityLogType.MENTOR_FORM:
      return `/users/${userId}/mentorForms/${activity.externalId}`;
    default:
      return '';
  }
};

export const getInitiatorRole = (roles: string[]) => {
  if (roles.includes(ROLES.TECH_ADMIN)) return 'Help Desk';
  if (roles.includes(ROLES.MANAGEMENT_ADMIN)) return 'Operations';
  if (supervisorRoles.some((role) => roles.includes(role))) return 'Supervisor';
  if (mentorRoles.some((role) => roles.includes(role))) return 'Therapist';
  return 'Patient';
};

export const hasFeedbackRecord = (type: ActivityLogType): boolean => {
  const feedbackTypes = [
    ActivityLogType.ACTIVITY_PRACTICE,
    ActivityLogType.PRACTICE,
    ActivityLogType.ACTIVITY_POLL,
    ActivityLogType.POLL,
  ];
  return feedbackTypes.includes(type);
};

export const isSupportedType = (type?: ActivityLogType | null): boolean => {
  if (!type) return false;
  const supportedTypes = [
    ActivityLogType.ACTIVITY_PRACTICE,
    ActivityLogType.PRACTICE,
    ActivityLogType.ACTIVITY_POLL,
    ActivityLogType.POLL,
    ActivityLogType.ACTIVITY_LESSON,
    ActivityLogType.LESSON,
  ];
  return supportedTypes.includes(type);
};